import { Button, Input, Layout, Row, Space, Typography } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ArrowLeftIconComponent from "../../../components/ArrowLeftIconComponent";
import { quickFileService } from "../../../services/quickFile.service";
import "./index.less";

interface CreatorCreateNewAlbumComponentProps {
  inlineMode?: boolean;
  onBack?: () => void;
  onCreated?: (albumId: string) => void;
}

const CreatorCreateNewAlbumComponent = ({ inlineMode = false, onBack, onCreated }: CreatorCreateNewAlbumComponentProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [albumName, setAlbumName] = useState<string>("");
  const [step, setStep] = useState<1 | 2>(1);
  const [albumType, setAlbumType] = useState<"audio" | "photos-videos" | null>(null);

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      return;
    }
    if (inlineMode && onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  const handleCreateAlbum = async () => {
    if (!albumName.trim() || !albumType) return;
    try {
      const res = await quickFileService.addAlbum(albumName, albumType);
      if (inlineMode && onCreated) {
        onCreated(res._id);
        return;
      }
      if (albumType === "photos-videos") {
        navigate("/creator/AddMedia/" + res._id, { replace: true });
      } else {
        navigate("/creator/AddMedia/" + res._id + "/audio", { replace: true, state: { albumName } });
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Layout className={`gradient-background-dark-blue ${inlineMode ? "inline" : ""}`}>
      <Layout.Header className="header background-transparent">
        <Row className="relative justify-content-center">
          <ArrowLeftIconComponent top={10} callback={handleBack} />
          <Typography.Text className="header-title-black-35">{t("New-album")}</Typography.Text>
        </Row>
      </Layout.Header>
      <Layout.Content className="content CreatorCreateNewAlbumContentWrapper background-transparent">
        {step === 1 ? (
          <Space className="w-full gap-21 mt-100" direction="vertical">
            <Row className="w-full justify-content-center">
              <Typography.Text className="font-18-bold text-white-color">{t("what-type-of-album-do-you-want-to-create?")}</Typography.Text>
            </Row>
            <div className="type-options">
              <div
                className={`type-option ${albumType === "audio" ? "selected" : ""}`}
                onClick={() => setAlbumType("audio")}
                role="button"
                tabIndex={0}
                aria-pressed={albumType === "audio"}
              >
                <Typography.Text className="font-16-bold">{t("audios")}</Typography.Text>
              </div>
              <div
                className={`type-option ${albumType === "photos-videos" ? "selected" : ""}`}
                onClick={() => setAlbumType("photos-videos")}
                role="button"
                tabIndex={0}
                aria-pressed={albumType === "photos-videos"}
              >
                <Typography.Text className="font-16-bold">{t("photos-videos")}</Typography.Text>
              </div>
            </div>
            <div className="bottom-cta">
              <Button
                className="authBtn mt-16"
                style={{ width: "100%", fontSize: 20, height: "65px" }}
                onClick={() => setStep(2)}
                disabled={!albumType}
              >
                {t("continue")}
              </Button>
            </div>
          </Space>
        ) : (
          <Space className="w-full gap-21 mt-100" direction="vertical">
            <Row className="w-full justify-content-center">
              <Typography.Text className="font-18-bold text-white-color">{t("give-your-album-a-name")}</Typography.Text>
            </Row>
            <Input
              className="titleInput"
              bordered={false}
              size="large"
              step="0.01"
              value={albumName}
              onChange={(e) => setAlbumName(e.target.value)}
              placeholder={t("my-name") || "My name"}
              autoFocus={true}
            />
            <div className={"divider-input"} />
            <div className="bottom-cta">
              <Button
                className="authBtn mt-16"
                style={{ width: "100%", fontSize: 20, height: "65px" }}
                onClick={handleCreateAlbum}
                disabled={!albumName.trim()}
              >
                {t("continue")}
              </Button>
            </div>
          </Space>
        )}
      </Layout.Content>
    </Layout>
  );
};

export default CreatorCreateNewAlbumComponent;
